// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useContext } from "react";
import { css } from "styled-components";
import ProductAccessoryUpsell, {
    ProductAccessoryUpsellPresentationProps,
} from "@store/Library/Components/ProductAccessoryUpsell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ProductDetailsBundle from "@store/Overrides/Components/ProductDetails/ProductDetailsBundle";
import getHasAccessoriesProperty from "@store/Framework/Common/Utilities/getHasAccessoriesProperty";
import RequestPackagingAdviceButton from "@store/Library/Components/RequestPackagingAdviceButton";
import { useIsAccessoryUpsellEnabled, useIsPackagingAdviceEnabled } from "@store/Framework/Store/Context/ContextHooks";

const ProductDescription = getWidgetComponent("ProductDetails/ProductDetailsDescription");
const ProductDetailsAddToCartOptionsAndButton = getWidgetComponent(
    "ProductDetails/ProductDetailsAddToCartOptionsAndButton",
);
const ProductDetailsPrice = getWidgetComponent("ProductDetails/ProductDetailsPrice");
const ProductDetailsDeliveryTerms = getWidgetComponent("ProductDetails/ProductDetailsDeliveryTerms");
const ProductDetailsFuturePrice = getWidgetComponent("ProductDetails/ProductDetailsFuturePrice");
const ProductDetailsAddToListLink = getWidgetComponent("ProductDetails/ProductDetailsAddToListLink");
const ProductDetailsLeadTime = getWidgetComponent("ProductDetails/ProductDetailsLeadTime");
const ProductDetailsFullTruckLoad = getWidgetComponent("ProductDetails/ProductDetailsFullTruckLoad");
const ProductDetailsShipsFrom = getWidgetComponent("ProductDetails/ProductDetailsShipsFrom");

export interface ProductDetailsInformationsProps {
    termsAndConditionsLink?: LinkFieldValue;
    overrideLinkLabel?: string;
}

export interface ProductDetailsInformationsStyles {
    wrapper?: InjectableCss;
    productDetails?: InjectableCss;
    productListAndShareWrapper?: InjectableCss;
    additionalInfosWrapper?: InjectableCss;
    additionalInfosTitle?: TypographyPresentationProps;
    priceAndTermsWrapper?: InjectableCss;
    productAccessoryUpsell?: ProductAccessoryUpsellPresentationProps;
}

const styles: ProductDetailsInformationsStyles = {
    productDetails: {
        css: css`
            ${x => spacingStyles(null, x.theme.design.product.detail.contentSpacing)}
        `,
    },
    productAccessoryUpsell: {
        extendedStyles: {
            wrapper: {
                css: css`
                    margin: ${pxToRem(30, 0, 25)};
                `,
            },
        },
    },
    additionalInfosWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            border-bottom: 1px solid ${getColor("common.border")};
            margin-top: ${pxToRem(16)};
            padding: ${pxToRem(16)} 0;
        `,
    },
    additionalInfosTitle: {
        forwardAs: "h2",
        variant: "h4",
        weight: "bold",
        css: css`
            margin-bottom: ${pxToRem(10)};
        `,
    },
    priceAndTermsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: ${pxToRem(12)};
            margin-bottom: ${pxToRem(15)};

            ${breakpoint("md")} {
                flex-direction: row;

                > div {
                    margin-bottom: ${pxToRem(6)};
                }
            }
        `,
    },
    productListAndShareWrapper: {
        css: css`
            display: flex;
            gap: ${pxToRem(24)};
        `,
    },
};

const ProductDetailsInformations: React.FC<ProductDetailsInformationsProps> = ({
    termsAndConditionsLink,
    overrideLinkLabel,
}) => {
    const { product, productInfo } = useContext(ProductContext);
    const isPackagingAdviceEnabled = useIsPackagingAdviceEnabled();
    const isAccessoryUpsellEnabled = useIsAccessoryUpsellEnabled();
    const hasAccessories = getHasAccessoriesProperty(product);
    const productId = product.id || productInfo.productId;
    const warehouse = productInfo.pricing?.additionalResults?.warehouseName;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.productDetails}>
                <StyledWrapper {...styles.priceAndTermsWrapper}>
                    <ProductDetailsPrice />
                    <ProductDetailsDeliveryTerms
                        termsAndConditionsLink={termsAndConditionsLink}
                        overrideLinkLabel={overrideLinkLabel}
                    />
                </StyledWrapper>
                <ProductDetailsFuturePrice />
                <ProductDescription />
                <ProductDetailsBundle />
            </StyledWrapper>
            {isAccessoryUpsellEnabled && hasAccessories && productId && warehouse && (
                <ProductAccessoryUpsell
                    {...styles.productAccessoryUpsell}
                    productId={productId}
                    showCheckbox
                    warehouse={warehouse}
                />
            )}
            <StyledWrapper {...styles.productDetails}>
                <ProductDetailsAddToCartOptionsAndButton />
            </StyledWrapper>
            <StyledWrapper {...styles.productListAndShareWrapper}>
                <ProductDetailsAddToListLink />
                {isPackagingAdviceEnabled && <RequestPackagingAdviceButton isLink />}
            </StyledWrapper>
            <StyledWrapper {...styles.additionalInfosWrapper}>
                <Typography {...styles.additionalInfosTitle}>{translate("Product Fulfillment Information")}</Typography>
                <ProductDetailsShipsFrom />
                <ProductDetailsFullTruckLoad />
                <ProductDetailsLeadTime />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductDetailsInformations;
